
import React from 'react'
import { Link } from 'react-router-dom'
import {Globe, Shield, Users, BookOpen, ArrowRight, CheckCircle} from 'lucide-react'
import HeroNursingHive from '../components/HeroNursingHive'

const Home = () => {

  const destinations = [
    {
      country: "USA",
      requirements: ["NCLEX-RN", "CGFNS/VisaScreen", "English proficiency"],
      color: "bg-blue-50 border-blue-200"
    },
    {
      country: "UK/Ireland",
      requirements: ["NMC/NMBI Registration", "IELTS/OET", "CBT/OSCE"],
      color: "bg-green-50 border-green-200"
    },
    {
      country: "GCC",
      requirements: ["Dataflow/PSV", "Good Standing", "Local licensing"],
      color: "bg-yellow-50 border-yellow-200"
    },
    {
      country: "Canada",
      requirements: ["NCLEX-RN", "NNAS/Provincial", "English/French"],
      color: "bg-red-50 border-red-200"
    }
  ]

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <HeroNursingHive />

      {/* Nurses by Region Band */}
      <section className="py-7 px-4" style={{ backgroundColor: "#F7FBFF" }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link
              to="/south-asia"
              className="bg-white rounded-lg p-6 border border-gray-200 hover:border-primary-teal hover:shadow-md transition-all"
            >
              <h3 className="text-lg font-bold text-primary-navy mb-2">South Asia</h3>
              <p className="text-sm text-primary-slate">Pakistan • India • Nepal • Sri Lanka • Bangladesh</p>
            </Link>
            <Link
              to="/countries/southeast-asia"
              className="bg-white rounded-lg p-6 border border-gray-200 hover:border-primary-teal hover:shadow-md transition-all"
            >
              <h3 className="text-lg font-bold text-primary-navy mb-2">Southeast Asia</h3>
              <p className="text-sm text-primary-slate">Philippines • Indonesia • Malaysia • Thailand • Vietnam</p>
            </Link>
            <Link
              to="/countries/gcc"
              className="bg-white rounded-lg p-6 border border-gray-200 hover:border-primary-teal hover:shadow-md transition-all"
            >
              <h3 className="text-lg font-bold text-primary-navy mb-2">GCC</h3>
              <p className="text-sm text-primary-slate">KSA • UAE • Qatar • Kuwait • Oman • Bahrain</p>
            </Link>
            <Link
              to="/countries/africa"
              className="bg-white rounded-lg p-6 border border-gray-200 hover:border-primary-teal hover:shadow-md transition-all"
            >
              <h3 className="text-lg font-bold text-primary-navy mb-2">Africa</h3>
              <p className="text-sm text-primary-slate">Kenya • Nigeria • Ghana • Ethiopia • South Africa</p>
            </Link>
            <Link
              to="/countries/united-states"
              className="bg-white rounded-lg p-6 border border-gray-200 hover:border-primary-teal hover:shadow-md transition-all"
            >
              <h3 className="text-lg font-bold text-primary-navy mb-2">United States</h3>
              <p className="text-sm text-primary-slate">Already in the U.S.? Endorsement & placements</p>
            </Link>
            <Link
              to="/uk-europe"
              className="bg-white rounded-lg p-6 border border-gray-200 hover:border-primary-teal hover:shadow-md transition-all"
            >
              <h3 className="text-lg font-bold text-primary-navy mb-2">UK & Europe</h3>
              <p className="text-sm text-primary-slate">NMC/CBT/OSCE pathways • EU opportunities</p>
            </Link>
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link
              to="/signup"
              className="bg-primary-teal text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#0da59f] transition-colors text-center"
            >
              Register Free / Sign In
            </Link>
            <Link
              to="/resources"
              className="bg-white text-primary-navy border-2 border-primary-navy px-6 py-3 rounded-lg font-semibold hover:bg-primary-light transition-colors text-center"
            >
              See Resources
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-primary-navy text-center mb-16">
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-primary-teal text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                1
              </div>
              <h3 className="text-xl font-semibold text-primary-navy mb-4">Join Free</h3>
              <p className="text-primary-slate">
                Create your profile in under a minute. Tell us about your background and career goals.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-primary-teal text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                2
              </div>
              <h3 className="text-xl font-semibold text-primary-navy mb-4">Get Verified</h3>
              <p className="text-primary-slate">
                Follow our step-by-step guides for NCLEX, CGFNS/VisaScreen, Dataflow, IELTS/OET.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-primary-teal text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                3
              </div>
              <h3 className="text-xl font-semibold text-primary-navy mb-4">Get Matched</h3>
              <p className="text-primary-slate">
                When you're verified, we introduce you to ethical recruiters in your target countries.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Destination Tracks */}
      <section className="py-20 px-4 bg-primary-light">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-primary-navy text-center mb-16">
            Choose Your Destination
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {destinations.map((dest, index) => (
              <div key={index} className={`${dest.color} border rounded-xl p-6 hover:shadow-lg transition-shadow`}>
                <h3 className="text-xl font-bold text-primary-navy mb-4">{dest.country}</h3>
                <ul className="space-y-2 mb-4">
                  {dest.requirements.map((req, idx) => (
                    <li key={idx} className="text-primary-slate text-sm flex items-start">
                      <CheckCircle className="h-4 w-4 text-primary-teal mr-2 mt-0.5 flex-shrink-0" />
                      {req}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/resources"
                  className="text-primary-teal font-semibold text-sm hover:text-primary-navy transition-colors"
                >
                  See requirements →
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Nursing Hive */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-primary-navy text-center mb-16">
            Why Nursing Hive
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <Users className="h-12 w-12 text-primary-teal mx-auto mb-4" />
              <h3 className="font-semibold text-primary-navy mb-2">Open to All</h3>
              <p className="text-primary-slate text-sm">From students to senior nurses</p>
            </div>
            <div className="text-center">
              <Shield className="h-12 w-12 text-primary-teal mx-auto mb-4" />
              <h3 className="font-semibold text-primary-navy mb-2">Private Until Ready</h3>
              <p className="text-primary-slate text-sm">Your data stays private until you opt-in</p>
            </div>
            <div className="text-center">
              <BookOpen className="h-12 w-12 text-primary-teal mx-auto mb-4" />
              <h3 className="font-semibold text-primary-navy mb-2">Clinician-Led</h3>
              <p className="text-primary-slate text-sm">Built by healthcare professionals</p>
            </div>
            <div className="text-center">
              <Globe className="h-12 w-12 text-primary-teal mx-auto mb-4" />
              <h3 className="font-semibold text-primary-navy mb-2">Multi-Country</h3>
              <p className="text-primary-slate text-sm">Multiple pathway options</p>
            </div>
          </div>
          <div className="text-center mt-12">
            <p className="text-lg text-primary-slate mb-6">
              Join 1,000+ nurses starting their global journey
            </p>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 px-4 bg-primary-teal">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to start? Registration is free and takes 60 seconds.
          </h2>
          <Link
            to="/signup"
            className="bg-white text-primary-teal px-8 py-4 rounded-lg text-lg font-semibold hover:bg-primary-light transition-colors inline-flex items-center"
          >
            Register Free / Sign In
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>

      {/* FAQ Teaser */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-primary-navy mb-6">
            Have Questions?
          </h2>
          <p className="text-lg text-primary-slate mb-8">
            Find answers to common questions about registration, verification, and getting matched with recruiters.
          </p>
          <Link
            to="/faq"
            className="bg-primary-teal text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#0da59f] transition-colors inline-flex items-center"
          >
            View All FAQs
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  )
}

export default Home
